import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, BookOpen, ChevronRight, Library, Trash2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useFiscalYear } from "@/context/FiscalYearContext";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

function StaffTrainings() {
  const { fiscalYear } = useFiscalYear();
  const [items, setItems] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!fiscalYear) return;
    api.get(`/my/assignments?fiscal_year=${fiscalYear}`).then(({ data }) => setItems(data));
  }, [fiscalYear]);
  if (!items) return <Loading />;
  return (
    <div>
      <PageHeader title="研修" subtitle="あなたに割り当てられた研修" />
      {items.length ? (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:shadow-md transition-shadow">
              <div className="min-w-0">
                <div className="font-medium text-slate-800">{a.training?.name}</div>
                <div className="text-xs text-slate-400 mt-0.5">受講期限：{a.due_date || "-"}</div>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={a.status} />
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid={`learn-btn-${a.training_id}`} onClick={() => navigate(`/learn/${a.training_id}`)}>
                  開く <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : <EmptyState title="割り当てられた研修はありません" />}
    </div>
  );
}

function AdminTrainings() {
  const { fiscalYear } = useFiscalYear();
  const [items, setItems] = useState(null);
  const [offices, setOffices] = useState([]);
  const [masters, setMasters] = useState([]);
  const [mOpen, setMOpen] = useState(false);
  const [mForm, setMForm] = useState({ master_id: "", office_id: "", due_date: "" });
  const navigate = useNavigate();

  const load = useCallback(() => {
    if (!fiscalYear) return;
    api.get(`/trainings?fiscal_year=${fiscalYear}`).then(({ data }) => setItems(data));
  }, [fiscalYear]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/offices").then(({ data }) => setOffices(data)); }, []);
  useEffect(() => { api.get("/master-trainings/available").then(({ data }) => setMasters(data)); }, []);

  const fromMaster = async () => {
    if (!mForm.master_id || !mForm.office_id) { toast.error("教材と事業所は必須です"); return; }
    try {
      const { data } = await api.post("/trainings/from-master", { ...mForm, fiscal_year: fiscalYear });
      toast.success("マスター教材から研修を作成しました");
      setMOpen(false);
      setMForm({ master_id: "", office_id: "", due_date: "" });
      navigate(`/trainings/${data.training_id}`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const remove = async (t, e) => {
    e.stopPropagation();
    if (!window.confirm(`研修「${t.name}」を削除しますか？\n\n関連する動画・設問・資料・割当・受講記録・修了証もすべて削除されます。この操作は元に戻せません。`)) return;
    try { await api.delete(`/trainings/${t.id}`); toast.success("研修を削除しました"); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  if (!items) return <Loading />;
  const officeName = (id) => offices.find((o) => o.id === id)?.name || "-";

  return (
    <div>
      <PageHeader
        title="研修管理" subtitle={`${fiscalYear}年度の研修一覧`}
        actions={
          <>
            <Dialog open={mOpen} onOpenChange={setMOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="from-master-btn"><Library className="w-4 h-4 mr-1" /> マスターから追加</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>マスター教材から研修を作成</DialogTitle></DialogHeader>
                <p className="text-sm text-slate-500">運営が提供する標準研修コンテンツ（動画・設問・資料）をコピーして、自社の研修として追加します。</p>
                <div className="space-y-3 mt-1">
                  <div>
                    <Label>研修コンテンツ *</Label>
                    <Select value={mForm.master_id} onValueChange={(v) => setMForm({ ...mForm, master_id: v })}>
                      <SelectTrigger className="mt-1" data-testid="master-select"><SelectValue placeholder="教材を選択" /></SelectTrigger>
                      <SelectContent>{masters.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`master-opt-${m.id}`}>{m.name}（{m.category}）</SelectItem>)}</SelectContent>
                    </Select>
                    {!masters.length && <p className="text-xs text-slate-400 mt-1">利用可能なマスター教材がありません</p>}
                  </div>
                  <div>
                    <Label>事業所 *</Label>
                    <Select value={mForm.office_id} onValueChange={(v) => setMForm({ ...mForm, office_id: v })}>
                      <SelectTrigger className="mt-1" data-testid="master-office-select"><SelectValue placeholder="事業所を選択" /></SelectTrigger>
                      <SelectContent>{offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>受講期限</Label><Input type="date" value={mForm.due_date} onChange={(e) => setMForm({ ...mForm, due_date: e.target.value })} className="mt-1" data-testid="master-due" /></div>
                </div>
                <DialogFooter><Button onClick={fromMaster} className="bg-blue-600 hover:bg-blue-700" data-testid="save-from-master-btn">この教材で作成</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        }
      />
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {items.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>研修名</TableHead><TableHead>事業所</TableHead><TableHead>状態</TableHead>
                <TableHead className="text-center">動画</TableHead><TableHead className="text-center">設問</TableHead><TableHead className="text-center">割当</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((t) => (
                <TableRow key={t.id} data-testid={`training-row-${t.id}`} className="cursor-pointer" onClick={() => navigate(`/trainings/${t.id}`)}>
                  <TableCell className="font-medium text-slate-800">{t.name}</TableCell>
                  <TableCell className="text-slate-500">{officeName(t.office_id)}</TableCell>
                  <TableCell><StatusBadge status={t.status} /></TableCell>
                  <TableCell className="text-center">{t.video_count}</TableCell>
                  <TableCell className="text-center">{t.question_count}</TableCell>
                  <TableCell className="text-center">{t.assigned_count}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={(e) => remove(t, e)} className="text-rose-500 hover:text-rose-600 p-1 rounded hover:bg-rose-50 transition-colors" data-testid={`training-delete-${t.id}`} title="研修を削除"><Trash2 className="w-4 h-4" /></button>
                      <ChevronRight className="w-4 h-4 text-slate-400 cursor-pointer" onClick={() => navigate(`/trainings/${t.id}`)} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : <EmptyState title="研修がありません" hint="「研修を作成」から追加してください" />}
      </div>
    </div>
  );
}

export default function Trainings() {
  const { user } = useAuth();
  return user.role === "staff" ? <StaffTrainings /> : <AdminTrainings />;
}
